import React, { useEffect } from 'react';
import { Form, FormGroup, Input, Label, Card, CardBody, CardTitle, CardSubtitle, CardText, Button, CardGroup, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import { TEST_DRIVE_ACTIVITIES, getPointingActivityBtns, getSwitchActivityBtns, getTextActivityBtns, getYourActivityBtns } from './ConstForTestDriveSection';
import { AM_CATEGORIES } from './ConstAccessMethodsMock';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_TRY_ACTIVITY_HELP = `
  <div>
    <p>
    An “activity” is what you actually do during the Test-drive.  
    Just like test-driving a car might involve some city driving and some highway driving, a Test-drive can include more than one activity.   
    </p>
    <p>In Access Navigator, we have two general types of activities:
      <ol>
        <li>The “in-app” activities are specific tasks that run directly inside the app and take detailed measurements.  
          For example, the basic text entry task will provide a simple typing test, and measure the speed and accuracy during the test.
        </li>
        <li> “Your own activity” is something you set up, outside of Access Navigator.  
          This might be something like playing computer solitaire, or browsing a favorite website, or typing a text message – whatever the client might enjoy that also relates to their goals.  
          In a low/no-tech context, this could be having the client give signals for yes/no responses, or press a switch to activate a chime.  
        </li>
      </ol>
    </p>
    <p>
    Whatever activity you choose, try to make sure it relates to the client’s goals (including the priority activity that you chose earlier in the session) 
    and that it is motivating and meaningful to the client. 
    </p>
    <p><strong>Note: </strong>If your priority activity is low/no-tech (like give yes/no responses), the "in-app" activities are not relevant, so they will not show as options on this page.</p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  isLowTechPath: PropTypes.bool,
  activeTestDriveCurrentActivity: PropTypes.string,
  onChangeActiveTestDriveCurrentActivity: PropTypes.func.isRequired,
  activeTestDriveCurrentActivityName: PropTypes.string,
  onChangeActiveTestDriveCurrentActivityName: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  isLowTechPath: false,
  activeTestDriveCurrentActivity: '',
  activeTestDriveCurrentActivityName: ''
};

//content sub-component with unique content for this page
function TestDriveTryActivityContent(props) {
  const firstName = props.firstName;
  const isLowTechPath = props.isLowTechPath;
  const activeAccessMethodCategory = props.activeAccessMethodCategory;
  const activeTestDriveCurrentActivity = props.activeTestDriveCurrentActivity;
  const onChangeActiveTestDriveCurrentActivity = props.onChangeActiveTestDriveCurrentActivity;
  const activeTestDriveCurrentActivityName = props.activeTestDriveCurrentActivityName;
  const onChangeActiveTestDriveCurrentActivityName = props.onChangeActiveTestDriveCurrentActivityName;

  const OWN_ACTIVITY = TEST_DRIVE_ACTIVITIES.OWN_ACTIVITY;

  const yourActivityBtns = getYourActivityBtns(firstName);
  const textActivityBtns = getTextActivityBtns(firstName);
  const pointingActivityBtns = getPointingActivityBtns(firstName);
  const switchActivityBtns = getSwitchActivityBtns(firstName);

  let builtInActivityBtns = [];
  if(!activeAccessMethodCategory){
    builtInActivityBtns = [...textActivityBtns, ...pointingActivityBtns, ...switchActivityBtns];
  }
  else if(activeAccessMethodCategory === AM_CATEGORIES.SWITCH_ACCESS){
    builtInActivityBtns = [...switchActivityBtns, ...textActivityBtns, ...pointingActivityBtns];
  }
  else
    builtInActivityBtns = [...textActivityBtns, ...pointingActivityBtns];

  return(
    <>
    <p>Try to choose something that is:</p>
    <ul>
      <li>motivating and meaningful to {firstName}</li>
      <li>directly related to the Priority Activity you've defined for this session</li>
    </ul>
    <Form>
      <FormGroup tag="fieldset">
        <hr />
        {(!isLowTechPath) ?
          <>
          <p><strong>In-app activities. </strong>Run within this app and provide detailed measurements.</p>
          <Row xs="1" lg={builtInActivityBtns.length}>
            {builtInActivityBtns.map((btn, index) =>  
            <Col key={'ba'+index}>
              <FormGroup check>
                <Label for={btn.value}>
                  <Input
                    className="aa-card-input-element"
                    id={btn.value}
                    value={btn.value}
                    checked={activeTestDriveCurrentActivity===btn.value}
                    onChange={onChangeActiveTestDriveCurrentActivity}
                    name="active-activity-id"
                    aria-label={btn.label}
                    type="radio"
                  />
                  <Card className="aa-clickable">
                    <CardBody>
                      <CardTitle tag="h5">
                        {btn.label}
                      </CardTitle>
                      <CardSubtitle
                        className="mb-2 text-muted"
                        tag="h6"
                      >
                        {btn.sublabel}
                      </CardSubtitle>
                      <CardText>
                        {btn.text}
                      </CardText>
                    </CardBody>
                  </Card>
                </Label>
              </FormGroup>
            </Col>
            )}
          </Row>
          <hr/>
          </>
          : <span></span>
        }
        <p><strong>Your own activity. </strong>A client's preferred activity or a free exploration using the access method.</p>
        <Row>
          {yourActivityBtns.map((btn, index) =>  
          <Col key={'ya'+index}>
            <FormGroup check>
              <Label for={btn.value}>
                <Input
                  className="aa-card-input-element"
                  id={btn.value}
                  value={btn.value}
                  checked={activeTestDriveCurrentActivity===btn.value}
                  onChange={onChangeActiveTestDriveCurrentActivity}
                  name="active-activity-id"
                  aria-label={btn.label}
                  type="radio"
                />
                <Card className="aa-clickable">
                  <CardBody>
                    <CardTitle tag="h5">
                      {btn.label}
                    </CardTitle>
                    <CardSubtitle
                      className="mb-2 text-muted"
                      tag="h6"
                    >
                      {btn.sublabel}
                    </CardSubtitle>
                    <CardText>
                      {btn.text}
                    </CardText>
                  </CardBody>
                </Card>
              </Label>
            </FormGroup>
          </Col>
          )}
        </Row>
      </FormGroup>
      {(activeTestDriveCurrentActivity===OWN_ACTIVITY) ? 
        <FormGroup className="aa-fade-in">
          <Label for="activity-name" className="aa-bold-text">
            Name for your own activity:
          </Label>
          <Input
            id="activity-name"
            value= {activeTestDriveCurrentActivityName}
            onChange= {onChangeActiveTestDriveCurrentActivityName}
            name="activity-name"
            type="textfield"
          />
        </FormGroup>
      : <span></span>}

    </Form>
    <p><strong>Note: </strong>You'll be able to choose more activities later if you want.</p>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveTryActivity(props) {
  const firstName = props.firstName;
  const isLowTechPath = props.isLowTechPath;
  const activeAccessMethodCategory = props.activeAccessMethodCategory;
  const activeTestDriveCurrentActivity = props.activeTestDriveCurrentActivity;
  const onChangeActiveTestDriveCurrentActivity = props.onChangeActiveTestDriveCurrentActivity;
  const activeTestDriveCurrentActivityName = props.activeTestDriveCurrentActivityName;
  const onChangeActiveTestDriveCurrentActivityName = props.onChangeActiveTestDriveCurrentActivityName;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-road aa-fa-3x",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Choose an activity for this Test-drive`,
    subTitle: ``,
    learnMore: {linkText: "Learn More about choosing Test-drive activities",
                title: "Choosing an activity",
                content: TD_TRY_ACTIVITY_HELP
                },
    nextRoute: "",
    sidebar: false
  }

  console.log("Rendering TestDriveTryActivity, activity: " + activeTestDriveCurrentActivity);

  useEffect(() => {
    return () => {
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveTryActivityContent 
          firstName = {firstName}
          isLowTechPath = {isLowTechPath}
          activeAccessMethodCategory = {activeAccessMethodCategory}
          activeTestDriveCurrentActivity = {activeTestDriveCurrentActivity}
          onChangeActiveTestDriveCurrentActivity = {onChangeActiveTestDriveCurrentActivity}
          activeTestDriveCurrentActivityName = {activeTestDriveCurrentActivityName}
          onChangeActiveTestDriveCurrentActivityName = {onChangeActiveTestDriveCurrentActivityName}
        />
      }
      sidebarComponent={
        dev.showDevSidebar 
        &&
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
      }
      onContinue={onContinue}  
    />
    </>
  );
}

TestDriveTryActivity.propTypes = propTypes;
TestDriveTryActivity.defaultProps = defaultProps;

export default TestDriveTryActivity;